import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, use, useEffect, useRef, type ReactNode } from "react";
import config from "./config";
import { type ForthLeafIdentity } from "./forthLeafIdentity";
import {
  acquireForthLeafAccessToken,
  createForthLeafServiceClient,
} from "./forthLeafServiceRequest";

const featureStaleTimeMs = 60 * 1000;
const featureRetryCount = 2;

export type FeatureQueryKey = readonly ["forthLeafFeatures", string | null, string | null];
export type ForthLeafFeatureStatus = "disabled" | "enabled" | "error" | "loading";

interface ForthLeafFeatureContextValue {
  getAccessTokenSilently: () => Promise<string>;
  identity: ForthLeafIdentity | undefined;
}

interface ForthLeafFeatureState {
  retry: () => void;
  status: ForthLeafFeatureStatus;
}

const ForthLeafFeatureContext = createContext<ForthLeafFeatureContextValue | undefined>(undefined);

export const createForthLeafFeaturesQueryKey = (
  identity: ForthLeafIdentity | undefined,
): FeatureQueryKey => ["forthLeafFeatures", identity?.subject ?? null, identity?.email ?? null];

export const ForthLeafFeatureProvider = ({
  children,
  getAccessTokenSilently,
  identity,
}: ForthLeafFeatureContextValue & { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const previousQueryKey = useRef<FeatureQueryKey | undefined>(undefined);
  const identitySubject = identity?.subject;
  const identityEmail = identity?.email;

  useEffect(() => {
    const currentQueryKey = identity ? createForthLeafFeaturesQueryKey(identity) : undefined;
    const previousKey = previousQueryKey.current;

    if (
      previousKey &&
      (!currentQueryKey ||
        previousKey[1] !== currentQueryKey[1] ||
        previousKey[2] !== currentQueryKey[2])
    ) {
      void queryClient.cancelQueries({ queryKey: previousKey });
      queryClient.removeQueries({ queryKey: previousKey });
    }

    previousQueryKey.current = currentQueryKey;

    return () => {
      if (!currentQueryKey) {
        return;
      }

      void queryClient.cancelQueries({ queryKey: currentQueryKey });
      queryClient.removeQueries({ queryKey: currentQueryKey });
    };
  }, [identity, identityEmail, identitySubject, queryClient]);

  return (
    <ForthLeafFeatureContext value={{ getAccessTokenSilently, identity }}>
      {children}
    </ForthLeafFeatureContext>
  );
};

export const useForthLeafFeature = (featureName: string): ForthLeafFeatureState => {
  const featureContext = use(ForthLeafFeatureContext);

  if (!featureContext) {
    throw new Error("useForthLeafFeature must be used within ForthLeafFeatureProvider.");
  }

  const { getAccessTokenSilently, identity } = featureContext;
  const query = useQuery({
    enabled: Boolean(identity),
    queryKey: createForthLeafFeaturesQueryKey(identity),
    queryFn: async ({ signal }) => {
      if (!identity) {
        throw new Error("Forth Leaf identity is unavailable.");
      }

      const token = await acquireForthLeafAccessToken(getAccessTokenSilently, signal);
      const response = await createForthLeafServiceClient(config.featureServiceUrl, token).get<
        string[]
      >(`active?email=${encodeURIComponent(identity.email)}`, { signal });

      return response.data;
    },
    retry: featureRetryCount,
    staleTime: featureStaleTimeMs,
  });

  if (query.data) {
    return {
      retry: () => void query.refetch(),
      status: query.data.includes(featureName) ? "enabled" : "disabled",
    };
  }

  if (query.isError) {
    return { retry: () => void query.refetch(), status: "error" };
  }

  return { retry: () => void query.refetch(), status: "loading" };
};
